import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications } from '../services/notificationService';
import { useLocationContext } from '../contexts/LocationContext';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

const getDismissedKey = (locationId) => `dismissed_notifications_${locationId || 'default'}`;

const getDismissed = (locationId) => {
  try {
    return JSON.parse(localStorage.getItem(getDismissedKey(locationId))) || [];
  } catch {
    return [];
  }
};

const setDismissedStorage = (locationId, ids) => {
  localStorage.setItem(getDismissedKey(locationId), JSON.stringify(ids));
};

const SeverityIcon = ({ severity }) => {
  if (severity === 'urgent') {
    return (
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
        <span className="text-white text-xs font-bold">!</span>
      </span>
    );
  }
  if (severity === 'warning') {
    return (
      <span className="flex-shrink-0 w-0 h-0 border-l-[10px] border-r-[10px] border-b-[18px] border-l-transparent border-r-transparent border-b-yellow-500 relative">
        <span className="absolute text-white text-[9px] font-bold" style={{ top: '5px', left: '-3px' }}>!</span>
      </span>
    );
  }
  return (
    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
      <span className="text-white text-xs font-bold">i</span>
    </span>
  );
};

const severityBorder = {
  urgent: 'border-l-red-500',
  warning: 'border-l-yellow-500',
  info: 'border-l-blue-500',
};

const NotificationBell = () => {
  const { currentLocation } = useLocationContext();
  const locationId = currentLocation?.id;
  const [notifications, setNotifications] = useState([]);
  const [dismissed, setDismissedState] = useState(() => getDismissed(locationId));
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications();
      const all = data.notifications || [];
      setNotifications(all);

      // Cleanup stale dismissed IDs
      const activeIds = new Set(all.map((n) => n.id));
      setDismissedState((prev) => {
        const cleaned = prev.filter((id) => activeIds.has(id));
        setDismissedStorage(locationId, cleaned);
        return cleaned;
      });
    } catch {
      // Silently fail — bell just shows stale data
    }
  }, [locationId]);

  useEffect(() => {
    setDismissedState(getDismissed(locationId));
    fetchNotifications();
    const interval = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNotifications, locationId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const visible = notifications.filter((n) => !dismissed.includes(n.id));
  const badgeCount = visible.length;

  const handleItemClick = (link) => {
    navigate(link);
    setOpen(false);
  };

  const handleClearAll = () => {
    const allIds = notifications.map((n) => n.id);
    setDismissedState(allIds);
    setDismissedStorage(locationId, allIds);
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 250);
  };

  // Group by severity for display order
  const grouped = { urgent: [], warning: [], info: [] };
  visible.forEach((n) => {
    if (grouped[n.severity]) grouped[n.severity].push(n);
  });

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
        aria-label="Notifications"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {badgeCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full min-w-[20px]">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className={`fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 mt-2 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[28rem] flex flex-col transition-all duration-250 origin-top-right ${
            closing ? 'opacity-0 scale-y-0 scale-x-75' : 'opacity-100 scale-100'
          }`}
          style={{ transitionDuration: '250ms' }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              Notifications {badgeCount > 0 && <span className="text-gray-500">({badgeCount})</span>}
            </h3>
          </div>

          {/* Items */}
          <div className="overflow-y-auto flex-1">
            {badgeCount === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                All caught up — no notifications
              </div>
            ) : (
              [...grouped.urgent, ...grouped.warning, ...grouped.info].map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleItemClick(n.link)}
                  className={`w-full text-left px-4 py-3 border-l-4 ${severityBorder[n.severity]} hover:bg-gray-50 flex items-start gap-3 transition-colors`}
                >
                  <SeverityIcon severity={n.severity} />
                  <span className="text-sm text-gray-700 leading-snug">{n.title}</span>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {badgeCount > 0 && (
            <div className="px-4 py-2 border-t border-gray-200">
              <button
                onClick={handleClearAll}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-1"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
